// ===================================================
// GAM Command Center — מחברת (Pinkas) — vocabulary + smart quick-add
// Moved out of PinkasTable.tsx (v3) unchanged in behaviour, so the list, the
// note details and the tests share one copy.
// ===================================================
import type { PinkasAssignee, PinkasKind, PinkasPriority, PinkasStatus } from "@/lib/cc-pinkas-queries";

export const KINDS: { v: PinkasKind; label: string; emoji: string }[] = [
  { v: "note", label: "הערה", emoji: "📝" },
  { v: "idea", label: "רעיון", emoji: "💡" },
  { v: "task", label: "משימה", emoji: "✅" },
];
export const KIND_META: Record<PinkasKind, { label: string; emoji: string }> = {
  note: { label: "הערה", emoji: "📝" },
  idea: { label: "רעיון", emoji: "💡" },
  task: { label: "משימה", emoji: "✅" },
};
export const ASSIGNEES: { v: PinkasAssignee; label: string }[] = [
  { v: "both", label: "שנינו" },
  { v: "gal", label: "גל" },
  { v: "claude", label: "קלוד" },
];
export const ASSIGNEE_LABEL: Record<PinkasAssignee, string> = { gal: "גל", claude: "קלוד", both: "שנינו" };
export const STATUSES: { v: PinkasStatus; label: string }[] = [
  { v: "open", label: "פתוח" },
  { v: "doing", label: "בעבודה" },
  { v: "done", label: "בוצע" },
];
export const STATUS_NEXT: Record<PinkasStatus, PinkasStatus> = { open: "doing", doing: "done", done: "open" };
export const STATUS_LABEL: Record<PinkasStatus, string> = { open: "פתוח", doing: "בעבודה", done: "בוצע" };
/** Status dot colour as a design token. */
export const STATUS_TOKEN: Record<PinkasStatus, string> = {
  open: "var(--theme-text-faint)",
  doing: "var(--role-warning)",
  done: "var(--role-success)",
};
export const PRIORITIES: { v: PinkasPriority | "none"; label: string }[] = [
  { v: "none", label: "—" },
  { v: "low", label: "נמוך" },
  { v: "normal", label: "רגיל" },
  { v: "high", label: "גבוה" },
];
export const SORT_OPTIONS = [
  { v: "updated", label: "עודכן" },
  { v: "priority", label: "עדיפות" },
  { v: "status", label: "סטטוס" },
] as const;

/** Note colour is user data (stored as a name in cc_pinkas.color), not UI chrome. */
export const NOTE_COLORS: { v: string | null; hex: string; label: string }[] = [
  { v: null, hex: "transparent", label: "ללא צבע" },
  { v: "teal", hex: "#14b8a6", label: "טורקיז" },
  { v: "amber", hex: "#f59e0b", label: "ענבר" },
  { v: "rose", hex: "#f43f5e", label: "ורוד" },
  { v: "blue", hex: "#3b82f6", label: "כחול" },
  { v: "violet", hex: "#8b5cf6", label: "סגול" },
  { v: "green", hex: "#22c55e", label: "ירוק" },
];
export const noteColorHex = (c: string | null) => NOTE_COLORS.find((x) => x.v === c)?.hex ?? "transparent";

// ---- #2 smart quick-add parser ------------------------------------------
export function parseQuickAdd(raw: string): {
  text: string;
  kind?: PinkasKind;
  assignee?: PinkasAssignee;
  priority?: PinkasPriority;
  tags: string[];
} {
  let s = raw.trim();
  let kind: PinkasKind | undefined;
  let assignee: PinkasAssignee | undefined;
  let priority: PinkasPriority | undefined;
  const tags: string[] = [];

  // kind prefix: "משימה:" / "task:" / "רעיון:" / "idea:" / "הערה:" / "note:"
  const kindPre = s.match(/^(משימה|task|רעיון|idea|הערה|note)\s*[:：]\s*/i);
  if (kindPre) {
    const k = kindPre[1].toLowerCase();
    kind = /משימה|task/.test(k) ? "task" : /רעיון|idea/.test(k) ? "idea" : "note";
    s = s.slice(kindPre[0].length);
  }
  // assignee: @claude / @cl / @gal / @me / @גל / @both / @שנינו
  s = s
    .replace(/(^|\s)@(claude|cl|קלוד)\b/gi, () => {
      assignee = "claude";
      return " ";
    })
    .replace(/(^|\s)@(gal|me|גל)\b/gi, () => {
      assignee = "gal";
      return " ";
    })
    .replace(/(^|\s)@(both|שנינו)\b/gi, () => {
      assignee = "both";
      return " ";
    });
  // priority: trailing/standalone "!" or "!!" => high
  if (/(^|\s)!{1,2}(\s|$)/.test(s)) {
    priority = "high";
    s = s.replace(/(^|\s)!{1,2}(?=\s|$)/g, " ");
  }
  // tags: #word (hebrew/latin/digits)
  s = s.replace(/(^|\s)#([\p{L}\p{N}_-]+)/gu, (_m, _p, tag) => {
    tags.push(tag);
    return " ";
  });

  return { text: s.replace(/\s{2,}/g, " ").trim(), kind, assignee, priority, tags };
}
